import { useState } from "react";
import { useTimer } from "../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Clock, Play, Trash2, Edit3, AlertTriangle } from "lucide-react";

const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const formatDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours === 0) return `${minutes} minutos`;
  if (minutes === 0) return `${hours} hora${hours > 1 ? 's' : ''}`;
  return `${hours}h ${minutes}m`;
};

const InactivityModal = () => {
  const {
    showInactivityModal,
    setShowInactivityModal,
    pausedAtSeconds,
    timerProject,
    resumeFromAutoPause,
    resumeWithAdjustedTime,
    discardExcessTime,
    stopTimer,
  } = useTimer();

  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustHours, setAdjustHours] = useState(Math.floor(pausedAtSeconds / 3600));
  const [adjustMinutes, setAdjustMinutes] = useState(Math.floor((pausedAtSeconds % 3600) / 60));

  const handleAdjustTime = () => {
    const totalSeconds = (parseInt(adjustHours) || 0) * 3600 + (parseInt(adjustMinutes) || 0) * 60;
    resumeWithAdjustedTime(totalSeconds);
    setShowAdjust(false);
  };

  const handleStopAndSave = () => {
    stopTimer();
    setShowInactivityModal(false);
  };

  // Calculate suggested time (1 hour less than paused time)
  const suggestedSeconds = Math.max(0, pausedAtSeconds - 3600);

  return (
    <Dialog open={showInactivityModal} onOpenChange={setShowInactivityModal}>
      <DialogContent className="sm:max-w-md" data-testid="inactivity-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="font-serif text-xl">
                Timer pausado por inactividad
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-left pt-2">
            Detectamos que no hubo actividad durante 1 hora, por lo que pausamos automáticamente tu timer para evitar registrar tiempo incorrecto.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Current time info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Proyecto:</span>
              <span className="font-medium text-slate-900">{timerProject?.name || "Sin proyecto"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Tiempo registrado:</span>
              <span className="font-mono text-2xl font-bold text-[#002FA7]">
                {formatTime(pausedAtSeconds)}
              </span>
            </div>
            <p className="text-xs text-slate-500 pt-1">
              ({formatDuration(pausedAtSeconds)} de trabajo)
            </p>
          </div>

          {/* Adjust time section */}
          {showAdjust ? (
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <Label className="text-sm font-medium">Ajustar tiempo manualmente:</Label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Horas</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={adjustHours}
                    onChange={(e) => setAdjustHours(e.target.value)}
                    className="font-mono text-lg"
                    data-testid="adjust-hours-input"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Minutos</Label>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={adjustMinutes}
                    onChange={(e) => setAdjustMinutes(e.target.value)}
                    className="font-mono text-lg"
                    data-testid="adjust-minutes-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdjust(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleAdjustTime}
                  className="flex-1 btn-primary"
                  data-testid="confirm-adjust-button"
                >
                  Aplicar y continuar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">¿Qué deseas hacer?</p>
            </div>
          )}
        </div>

        {!showAdjust && (
          <DialogFooter className="flex-col sm:flex-col gap-2">
            {/* Primary action - Resume */}
            <Button
              onClick={resumeFromAutoPause}
              className="w-full btn-primary"
              data-testid="resume-timer-button"
            >
              <Play className="h-4 w-4 mr-2" />
              Continuar con {formatDuration(pausedAtSeconds)}
            </Button>

            {/* Secondary actions */}
            <div className="grid grid-cols-2 gap-2 w-full">
              <Button
                variant="outline"
                onClick={() => {
                  setAdjustHours(Math.floor(pausedAtSeconds / 3600));
                  setAdjustMinutes(Math.floor((pausedAtSeconds % 3600) / 60));
                  setShowAdjust(true);
                }}
                className="btn-outline"
                data-testid="adjust-time-button"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Ajustar tiempo
              </Button>
              <Button
                variant="outline"
                onClick={discardExcessTime}
                className="btn-outline"
                data-testid="discard-excess-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Usar {formatDuration(suggestedSeconds)}
              </Button>
            </div>

            {/* Stop timer option */}
            <Button
              variant="ghost"
              onClick={handleStopAndSave}
              className="w-full text-slate-500 hover:text-slate-700"
              data-testid="stop-timer-button"
            >
              <Clock className="h-4 w-4 mr-2" />
              Detener timer (descartar)
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InactivityModal;
